import React, { useEffect } from "react";
import {
  Modal,
  ButtonWithAction,
  Text,
  AutopayOnIcon,
  Box,
  Cluster,
  Paragraph,
  constants,
  util
} from "@thecb/components";

const { SEA_GREEN } = constants.colors;
const { titleCaseString } = util.general;
const { FONT_WEIGHT_REGULAR } = constants.styleConstants;

const AutopayModalModule = ({
  autoPayActive,
  autoPaySchedule,
  paymentPlanSchedule,
  toggleModal,
  modalOpen,
  deactivatePaymentSchedule,
  deactivatingSchedule,
  navigateToSettings,
  controlType = "tertiary",
  isMobile,
  isPaymentPlan,
  nextAutopayDate,
  dueDate,
  inactive,
  description,
  subDescription,
  disableActions = false,
  action,
  onClick,
  onKeyPress
}) => {
  const plan = isPaymentPlan ? "your payment plan" : "autopay";
  const shortPlan = isPaymentPlan ? "Payment Plan" : "Autopay";
  const deactivateText = `Stop ${shortPlan.toLowerCase()} for ${titleCaseString(
    description
  )}: ${titleCaseString(subDescription)}`;
  const activateText = `Set up ${shortPlan.toLowerCase()} for ${titleCaseString(
    description
  )}: ${titleCaseString(subDescription)}`;
  const nextDate = dueDate || nextAutopayDate;

  // Close modal when deactivation successfully completes.
  useEffect(() => {
    if (autoPayActive && deactivatingSchedule === false) {
      toggleModal(false);
    }
  }, [autoPayActive, deactivatingSchedule]);

  const modalExtraProps = {
    modalHeaderText: autoPayActive ? deactivateText : activateText,
    modalBodyText: autoPayActive ? (
      <>
        <Paragraph>
          Are you sure you want to stop {plan}?
          {!inactive && nextDate && nextDate !== "On"
            ? " Your next payment will be due on " + nextDate + "."
            : ""}
        </Paragraph>
        {!isPaymentPlan && (
          <>
            <br />
            <Paragraph>
              If your next autopayment is scheduled to run today, you may be
              billed today and autopay will then stop for the next billing
              cycle.
            </Paragraph>
          </>
        )}
      </>
    ) : (
      <Paragraph>Proceed to {plan} setup.</Paragraph>
    ),
    continueButtonText: autoPayActive ? `Stop ${shortPlan}` : "Add",
    useDangerButton: autoPayActive,
    continueAction: autoPayActive
      ? () => {
          deactivatePaymentSchedule(
            isPaymentPlan ? paymentPlanSchedule : autoPaySchedule,
            isPaymentPlan
          );
        }
      : navigateToSettings,
    isLoading: deactivatingSchedule
  };

  const hoverStyles = "text-decoration: underline;";
  const activeStyles = "text-decoration: underline;";

  const renderAutoPayControl = () => {
    switch (controlType) {
      case "secondary": {
        return (
          <ButtonWithAction
            text={
              autoPayActive ? `Turn off ${shortPlan}` : `Set Up ${shortPlan}`
            }
            variant="secondary"
            action={action || (() => toggleModal(true))}
            dataQa="Turn off Autopay"
            extraStyles={
              isMobile
                ? `flex-grow: 1; width: 100%; margin: 0;`
                : `flex-grow: 1; min-width: 165px;`
            }
            disabled={disableActions}
          />
        );
      }
      case "tertiary": {
        return (
          <ButtonWithAction
            text={autoPayActive ? `Manage ${shortPlan}` : `Set Up ${shortPlan}`}
            variant="tertiary"
            action={action || (() => toggleModal(true))}
            dataQa="Manage Autopay"
            extraStyles={isMobile && `flex-grow: 1; width: 100%;`}
            disabled={disableActions}
          />
        );
      }
      case "link": {
        return (
          <Box
            padding="0"
            role="button"
            tabIndex="0"
            onClick={
              onClick ||
              (() => {
                toggleModal(true);
              })
            }
            onKeyDown={
              onKeyPress ||
              (e => {
                e.key === "Enter" && toggleModal(true);
              })
            }
            hoverStyles={hoverStyles}
            activeStyles={activeStyles}
            extraStyles={"cursor: pointer; margin-bottom: 5px;"}
            dataQa={`${shortPlan} On`}
          >
            <Cluster
              justify={isMobile ? "flex-start" : "flex-end"}
              align="center"
            >
              <AutopayOnIcon />
              <Text
                variant="pS"
                color={SEA_GREEN}
                weight={FONT_WEIGHT_REGULAR}
                extraStyles={`padding-left: 0.25rem;`}
                disabled={disableActions}
              >
                {`${shortPlan} ${nextAutopayDate}`}
              </Text>
            </Cluster>
          </Box>
        );
      }
    }
  };
  return (
    <Modal
      showModal={() => toggleModal(true)}
      hideModal={() => toggleModal(false)}
      modalOpen={modalOpen}
      {...modalExtraProps}
    >
      {renderAutoPayControl()}
    </Modal>
  );
};

export default AutopayModalModule;
